import { useState, useCallback } from "react";
import type { IImagemProduto } from "interfaces/loja/produtos";
import {
  enviarImagensProdutoLoja,
  removerImagemProdutoLoja,
} from "services/lojaProduto";
import { useMaterialUIController, setMessage } from "context";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DropzoneGlobal from "components/DropzoneGlobal";
import ProdutoImageCropModal from "components/ProdutoImageCropModal";
import {
  validateImageFilesMaxDimensions,
  PRODUTO_IMAGE_MAX_WIDTH,
  PRODUTO_IMAGE_MAX_HEIGHT,
} from "helpers/validateImageDimensions";

import { getProdutoImageUploadPathFromUrl } from "helpers/produtoImagemUrl";

const PREVIEW_SIZE = 80;

const MAX_IMAGENS_POR_VARIACAO = 5;

export interface ImagensModalProps {
  open: boolean;
  onClose: () => void;
  produtoId: string;
  variacaoProdutoId: string | undefined;
  imagensExistentes: IImagemProduto[];
  onImagensEnviadas: () => void;
}

export default function ImagensModal({
  open,
  onClose,
  produtoId,
  variacaoProdutoId,
  imagensExistentes,
  onImagensEnviadas,
}: ImagensModalProps) {
  const [, dispatch] = useMaterialUIController();
  const [novosArquivos, setNovosArquivos] = useState<File[]>([]);
  const [dropzoneKey, setDropzoneKey] = useState(0);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [cropQueue, setCropQueue] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [removendoId, setRemovendoId] = useState<string | null>(null);
  const podeAdicionarMais = imagensExistentes.length < MAX_IMAGENS_POR_VARIACAO;
  const maxNovos = Math.max(
    0,
    MAX_IMAGENS_POR_VARIACAO - imagensExistentes.length
  );
  const totalApos = imagensExistentes.length + novosArquivos.length;

  const abrirFilaRecorte = useCallback((files: File[]) => {
    if (!files.length) return;
    setCropQueue(files);
    setCropFile(files[0]);
    setCropModalOpen(true);
  }, []);

  const processarArquivosImagem = useCallback(
    async (files: File[]) => {
      if (!files.length) return;

      const invalid = await validateImageFilesMaxDimensions(
        files,
        PRODUTO_IMAGE_MAX_WIDTH,
        PRODUTO_IMAGE_MAX_HEIGHT
      );

      const invalidSet = new Set(invalid.map((i) => i.file));

      const valid = files.filter((f) => !invalidSet.has(f));

      if (valid.length > 0) {
        setNovosArquivos((prev) => {
          const next = [...prev, ...valid];

          if (next.length > maxNovos) {
            setMessage(dispatch, {
              open: true,
              severity: "warning",
              text: `Máximo de ${maxNovos} nova(s) imagem(ns) nesta variação.`,
            });

            return next.slice(0, maxNovos);
          }
          return next;
        });
      }

      setDropzoneKey((k) => k + 1);

      if (invalid.length > 0) {
        abrirFilaRecorte(invalid.map((i) => i.file));

        setMessage(dispatch, {
          open: true,
          severity: "info",
          text:
            invalid.length === 1
              ? "A imagem excede o tamanho permitido. Ajuste o recorte no quadro."
              : `${invalid.length} imagens excedem o tamanho permitido. Ajuste o recorte de cada uma.`,
        });
      }
    },

    [abrirFilaRecorte, dispatch, maxNovos]
  );

  const handleCropConfirm = useCallback(
    (cropped: File) => {
      setNovosArquivos((prev) => {
        const next = [...prev, cropped];

        if (next.length > maxNovos) {
          setMessage(dispatch, {
            open: true,
            severity: "warning",
            text: `Máximo de ${maxNovos} nova(s) imagem(ns) nesta variação.`,
          });
          return next.slice(0, maxNovos);
        }
        return next;
      });

      setCropQueue((queue) => {
        const rest = queue.slice(1);

        if (rest.length > 0) {
          setCropFile(rest[0]);
          return rest;
        }

        setCropModalOpen(false);
        setCropFile(null);
        return [];
      });

      setMessage(dispatch, {
        open: true,
        severity: "success",
        text: "Imagem recortada e adicionada à lista.",
      });
    },
    [dispatch, maxNovos]
  );

  const handleCropClose = useCallback(() => {
    setCropModalOpen(false);
    setCropFile(null);
    setCropQueue([]);
  }, []);

  const resetUploadState = useCallback(() => {
    setNovosArquivos([]);
    setCropModalOpen(false);
    setCropFile(null);
    setCropQueue([]);
    setDropzoneKey((k) => k + 1);
  }, []);

  const handleFechar = useCallback(() => {
    resetUploadState();
    onClose();
  }, [onClose, resetUploadState]);

  const handleSalvar = useCallback(async () => {
    if (!variacaoProdutoId) {
      return;
    }

    if (novosArquivos.length === 0) {
      handleFechar();
      return;
    }

    if (totalApos > MAX_IMAGENS_POR_VARIACAO) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: `Máximo de ${MAX_IMAGENS_POR_VARIACAO} imagens por variação. Remova algumas ou envie menos arquivos.`,
      });
      return;
    }

    const invalidDimensions = await validateImageFilesMaxDimensions(
      novosArquivos,
      PRODUTO_IMAGE_MAX_WIDTH,
      PRODUTO_IMAGE_MAX_HEIGHT
    );

    if (invalidDimensions.length > 0) {
      abrirFilaRecorte(invalidDimensions.map((i) => i.file));
      setMessage(dispatch, {
        open: true,
        severity: "info",
        text: "Ajuste o recorte das imagens que excedem o tamanho permitido.",
      });
      return;
    }
    setSaving(true);

    try {
      await enviarImagensProdutoLoja({
        produtoId,
        variacaoProdutoId,
        arquivos: novosArquivos,
      });

      setMessage(dispatch, {
        open: true,
        severity: "success",
        text: "Imagens enviadas.",
      });

      onImagensEnviadas();
      handleFechar();
    } catch (err: any) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: err?.message ?? "Falha ao enviar imagens.",
      });
    } finally {
      setSaving(false);
    }
  }, [
    produtoId,
    variacaoProdutoId,
    novosArquivos,
    totalApos,
    onImagensEnviadas,
    handleFechar,
    dispatch,
    abrirFilaRecorte,
  ]);

  /** Variação ainda não salva no servidor (id temporário "var-...") */

  const variacaoNaoSalva =
    !variacaoProdutoId || String(variacaoProdutoId).startsWith("var-");

  const podeEnviar = Boolean(
    produtoId &&
    variacaoProdutoId &&
    !variacaoNaoSalva &&
    novosArquivos.length > 0
  );

  const handleRemoverImagem = useCallback(
    async (img: IImagemProduto) => {
      const pathRelativo = getProdutoImageUploadPathFromUrl(img.url);

      if (!pathRelativo) return;

      setRemovendoId(img.url);

      try {
        const result = await removerImagemProdutoLoja(
          encodeURIComponent(pathRelativo)
        );

        if (result.status === 200) {
          setMessage(dispatch, {
            open: true,
            severity: "success",
            text: "Imagem removida.",
          });

          onImagensEnviadas();
        }
      } catch (err: any) {
        setMessage(dispatch, {
          open: true,
          severity: "error",
          text: err?.message ?? "Falha ao remover imagem.",
        });
      } finally {
        setRemovendoId(null);
      }
    },

    [dispatch, onImagensEnviadas]
  );

  return (
    <>
      <Dialog open={open} onClose={handleFechar} maxWidth="sm" fullWidth>
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            Imagens da variação
            <IconButton size="small" onClick={handleFechar} aria-label="Fechar">
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>

        <DialogContent>
          {variacaoNaoSalva ? (
            <MDBox py={2}>
              <MDTypography
                variant="body2"
                color="info.main"
                fontWeight="medium"
                display="block"
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Icon fontSize="small">info</Icon>
                Salve as variações para ativar a inclusão de fotos. Depois de
                salvar, você poderá carregar as imagens desta variação aqui.
              </MDTypography>
            </MDBox>
          ) : (
            <>
              <MDTypography
                variant="caption"
                fontWeight="medium"
                display="block"
                mb={1}
              >
                Existentes ({imagensExistentes.length}/
                {MAX_IMAGENS_POR_VARIACAO})
              </MDTypography>

              <MDBox display="flex" flexWrap="wrap" gap={1} mb={2}>
                {imagensExistentes.length === 0 && (
                  <MDTypography variant="caption" color="text">
                    Nenhuma imagem ainda.
                  </MDTypography>
                )}

                {imagensExistentes.map((img, i) => (
                  <MDBox key={i} position="relative">
                    <img
                      src={img.url}
                      alt=""
                      style={{
                        width: PREVIEW_SIZE,
                        height: PREVIEW_SIZE,
                        objectFit: "cover",
                        borderRadius: 8,
                      }}
                    />

                    <IconButton
                      size="small"
                      sx={{
                        position: "absolute",
                        bottom: -8,
                        right: -8,
                        backgroundColor: "background.paper",
                        "&:hover": { backgroundColor: "grey.300" },
                      }}
                      onClick={() => handleRemoverImagem(img)}
                      disabled={removendoId === img.url}
                      aria-label="Remover imagem"
                    >
                      <Icon fontSize="small" color="error">
                        delete
                      </Icon>
                    </IconButton>
                  </MDBox>
                ))}
              </MDBox>

              <MDTypography
                variant="caption"
                fontWeight="medium"
                display="block"
                mb={1}
              >
                Novas (arraste e solte ou clique)
              </MDTypography>

              {!podeAdicionarMais ? (
                <MDTypography
                  variant="caption"
                  color="text"
                  display="block"
                  mb={1}
                >
                  Máximo de {MAX_IMAGENS_POR_VARIACAO} imagens por variação.
                  Remova uma para adicionar outra.
                </MDTypography>
              ) : (
                <>
                  <MDTypography
                    variant="caption"
                    color="text"
                    display="block"
                    mb={1}
                  >
                    Até {maxNovos} imagem(ns) · máx. {PRODUTO_IMAGE_MAX_WIDTH}×
                    {PRODUTO_IMAGE_MAX_HEIGHT} px (recorte automático se for
                    maior).
                  </MDTypography>

                  <MDBox
                    display="flex"
                    alignItems="stretch"
                    gap={2}
                    mb={2}
                    flexDirection={{ xs: "column", sm: "row" }}
                  >
                    <MDBox
                      sx={{
                        width: { xs: "100%", sm: 120 },
                        height: 120,
                        border: "2px dashed",
                        borderColor: "primary.main",
                        borderRadius: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "grey.50",
                        flexShrink: 0,
                      }}
                    >
                      <MDTypography
                        variant="caption"
                        color="primary"
                        fontWeight="bold"
                        textAlign="center"
                      >
                        Área permitida
                      </MDTypography>

                      <MDTypography
                        variant="caption"
                        color="text"
                        textAlign="center"
                      >
                        {PRODUTO_IMAGE_MAX_WIDTH} × {PRODUTO_IMAGE_MAX_HEIGHT}{" "}
                        px
                      </MDTypography>
                    </MDBox>

                    <MDBox flex={1} minWidth={0}>
                      <DropzoneGlobal
                        key={`${open}-${dropzoneKey}`}
                        accept="only-images"
                        maxFiles={maxNovos}
                        onFilesChange={processarArquivosImagem}
                      />
                    </MDBox>
                  </MDBox>

                  {novosArquivos.length > 0 && (
                    <MDBox mb={2}>
                      <MDTypography
                        variant="caption"
                        color="text"
                        display="block"
                        mb={0.5}
                      >
                        Prontas para envio ({novosArquivos.length}):
                      </MDTypography>

                      <MDBox display="flex" gap={1} flexWrap="wrap">
                        {novosArquivos.map((file, idx) => (
                          <MDBox
                            key={`${file.name}-${idx}`}
                            sx={{
                              width: PREVIEW_SIZE,
                              height: PREVIEW_SIZE,
                              borderRadius: 1,
                              overflow: "hidden",
                              border: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <img
                              src={URL.createObjectURL(file)}
                              alt={file.name}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                              onLoad={(e) =>
                                URL.revokeObjectURL(
                                  (e.target as HTMLImageElement).src
                                )
                              }
                            />
                          </MDBox>
                        ))}
                      </MDBox>
                    </MDBox>
                  )}
                </>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton variant="outlined" color="dark" onClick={handleFechar}>
            {variacaoNaoSalva ? "Fechar" : "Cancelar"}
          </MDButton>

          {!variacaoNaoSalva && (
            <MDButton
              variant="gradient"
              color="dark"
              onClick={handleSalvar}
              disabled={!podeEnviar || saving}
            >
              {saving ? "Enviando…" : "Salvar imagens"}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>

      <ProdutoImageCropModal
        open={cropModalOpen}
        file={cropFile}
        maxWidth={PRODUTO_IMAGE_MAX_WIDTH}
        maxHeight={PRODUTO_IMAGE_MAX_HEIGHT}
        onClose={handleCropClose}
        onConfirm={handleCropConfirm}
        onError={(text) =>
          setMessage(dispatch, { open: true, severity: "error", text })
        }
      />
    </>
  );
}
